import { Subscription } from 'rxjs';

import {
  GeoAreaEditSessionService,
  NEW_AREA_UID,
  shouldApplyIncoming
} from '@core/services/geo-area-edit-session/geo-area-edit-session.service';

import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';
import { MapSource } from '@features/components/visualization/mapbox/map-elements/map-source';
import { MapEvents } from '@features/components/visualization/mapbox/map-events';
import { GaliaArea } from '@features/components/visualization/mapbox/models/galia-area';
import { MapRegistryService } from '@features/components/visualization/mapbox/services/registry.service';

/**
 * How a click on a drawn boundary is answered. Supplied by whoever renders the map, since
 * only the page knows whether reshaping in place is offered here and how it is saved.
 */
export interface MapAreaInteraction {
  isEditable(uid: string): boolean;
  onSelect(uid: string): void;
}

export class MapAreas implements MapElement {
  private readonly layerFill: string;
  private readonly layerOutline: string;
  private readonly source: MapSource;
  private dataAreas: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: []
  };
  /** Everything the store last sent, unfiltered, so a session ending can re-render. */
  private lastReceived: GaliaArea[] = [];
  /** Zones the map is not about, shown as context on request. */
  private backdrop: GaliaArea[] = [];
  private subscription: Subscription | undefined;
  /** Whether the canvas cursor currently in place is the one this layer set. */
  private ownsCursor = false;

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly registry: MapRegistryService,
    private readonly session: GeoAreaEditSessionService,
    private readonly interaction: MapAreaInteraction
  ) {
    const layers = this.registry.getAreaLayers();
    this.layerFill = layers.fill;
    this.layerOutline = layers.outline;
    this.source = new MapSource(this.registry.getAreaSources().default, this.map);
  }

  init(): void {
    this.map.addSource(this.source.name, {
      type: 'geojson',
      data: this.dataAreas,
      promoteId: 'id'
    });

    this.map.addLayer({
      id: this.layerFill,
      type: 'fill',
      source: this.source.name,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.16
      }
    });

    this.map.addLayer({
      id: this.layerOutline,
      type: 'line',
      source: this.source.name,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2
      }
    });

    this.subscription = MapEvents.getInstance().on<GaliaArea[]>(
      'update-areas',
      this.updateAreas.bind(this)
    );
  }

  registerEvents(): void {
    this.map.on('click', this.layerFill, this.onAreaClick);
    this.map.on('mousemove', this.layerFill, this.onAreaHover);
    this.map.on('mouseleave', this.layerFill, this.onAreaLeave);
  }

  destroy(): void {
    this.map.off('click', this.layerFill, this.onAreaClick);
    this.map.off('mousemove', this.layerFill, this.onAreaHover);
    this.map.off('mouseleave', this.layerFill, this.onAreaLeave);
    this.subscription?.unsubscribe();
    this.source.cleanup();
    this.dataAreas = { type: 'FeatureCollection', features: [] };
    this.lastReceived = [];
    this.backdrop = [];
    this.ownsCursor = false;
  }

  /** Zones shown as context on a map whose subject is something else. */
  setBackdrop(areas: GaliaArea[]): void {
    this.backdrop = areas;
    this.render();
  }

  /**
   * Re-renders against the current session. Called from a component effect rather than by
   * re-emitting on the event bus, whose distinctUntilChanged would drop an identical array.
   */
  syncSession(): void {
    this.render();
  }

  /**
   * The cursor is the only advance notice that a boundary can be reshaped in place, so it
   * follows the same predicate the click does rather than promising an edit everywhere.
   *
   * Only a cursor this layer set is ever cleared: markers and routes sit inside zones and
   * set their own on enter, and an unconditional reset on every mousemove over the fill
   * would take theirs away for as long as the pointer stays within the boundary.
   */
  private readonly onAreaHover = (event: mapboxgl.MapLayerMouseEvent): void => {
    const uid = event.features?.[0]?.properties?.['id'];
    const editable = typeof uid === 'string' && this.interaction.isEditable(uid);
    if (editable === this.ownsCursor) {
      return;
    }
    this.ownsCursor = editable;
    this.map.getCanvas().style.cursor = editable ? 'pointer' : '';
  };

  private readonly onAreaLeave = (): void => {
    if (!this.ownsCursor) {
      return;
    }
    this.ownsCursor = false;
    this.map.getCanvas().style.cursor = '';
  };

  private readonly onAreaClick = (event: mapboxgl.MapLayerMouseEvent): void => {
    const uid = event.features?.[0]?.properties?.['id'];
    if (typeof uid === 'string' && this.interaction.isEditable(uid)) {
      this.interaction.onSelect(uid);
    }
  };

  private updateAreas(areas: GaliaArea[]): void {
    this.lastReceived = areas;
    this.render();
  }

  private render(): void {
    const activeUid = this.session.activeUid();
    const received = [...this.lastReceived, ...this.backdrop];
    // A zone being created has no stable uid to match on: the form still emits whatever
    // uid the user has typed, which shouldApplyIncoming cannot recognise as "under edit".
    // Withholding every area for the duration of a new-area session is the only way to
    // avoid drawing a second, lagging copy beneath the live Draw feature.
    const visible =
      activeUid === NEW_AREA_UID
        ? []
        : received.filter((area) => shouldApplyIncoming(area.uid, activeUid));
    this.dataAreas = this.toFeatureCollection(visible);
    this.source.updateSourceOnReady(this.dataAreas);
  }

  private toFeatureCollection(areas: GaliaArea[]): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: areas
        .filter((area) => area.ring.length >= 4)
        .map((area) => ({
          type: 'Feature' as const,
          id: area.uid,
          properties: { id: area.uid, name: area.name, color: area.color },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [
              area.ring.map((point) => [point.lng, point.lat]),
              ...(area.holes ?? []).map((hole) =>
                hole.map((point) => [point.lng, point.lat])
              )
            ]
          }
        }))
    };
  }
}
